import { isAfter } from "date-fns"
import { useVariantsByIds } from "~/hooks/useVariants"
import {
  getEndOfCurrentMonthUTC,
  getEndOfCurrentWeekUTC,
  getStartOfCurrentMonthUTC,
  getStartOfCurrentWeekUTC,
  startOfTodayUTC,
} from "~/utils/datetime"
import {
  dateSeries,
  generateInventorySeries,
  generateOrdersSeries,
  type InventoryMetric,
} from "~/utils/graphUtils"

import { type DateChartSeries, type PlotLine } from "../components/reusable/graphs/SuperChart"
import { filterDefined } from "~/utils/tsUtils"
import { formatSkuId } from "~/utils/helpers"
import { useGetClosingStockForDaysV2 } from "~/utils/closingStockUtils"
import { useFrozenForwardWindow } from "~/hooks/useFrozenForwardWindow"
import { isBeforeFirstKnownInventory } from "~/utils/variantTable/rowCalculators"
import useSafetyStockSeries from "~/hooks/useSafetyStockSeries"
import { useMemo } from "react"
import { api } from "~/utils/api"
import { useChartSeriesConfig } from "~/constants/colors"

/**
 * Collapses the inbound column series into one stacked column per period. Per-series rather
 * than SuperChart's chart-wide `isStacked`, which would stack the stock and demand series too.
 */
export const INBOUND_STACK = { stack: "inbound", stacking: "normal" } as const

type Props = {
  variantIds: string[]
  warehouseId: string | undefined
  channel: string | undefined
  monthly: boolean
  startDate: Date
  endDate: Date
  metric?: InventoryMetric
}

export default function useInventoryGraph({
  variantIds,
  warehouseId,
  channel,
  monthly,
  startDate,
  endDate,
  metric = "Units",
}: Props) {
  const chartConfig = useChartSeriesConfig()
  const variants = useVariantsByIds(variantIds)

  const { forwardStart, forwardEnd } = useFrozenForwardWindow()

  const closingStockData = useGetClosingStockForDaysV2({
    variantIds,
    warehouseId,
    startDate: forwardStart,
    endDate: forwardEnd,
    monthly,
    historicStart: startDate,
    costMode:
      metric === "Cost"
        ? { variants: variants.map((v) => ({ variantId: v.variantId, costPrice: v.costPrice })) }
        : undefined,
  })
  const safetyStockSeries = useSafetyStockSeries(
    startDate,
    endDate,
    monthly,
    variantIds,
    warehouseId,
  )

  const closingStockWithIncomingOrdersCurrentPeriod = useMemo(() => {
    const closingStock = closingStockData.closingStock
    const closingStockWithOrders = closingStockData.closingStockWithOrders

    if (!closingStock.length || !closingStockWithOrders.length)
      return { closingStock: [], closingStockWithOrders: [] }

    const currentPeriodStart = monthly ? getStartOfCurrentMonthUTC() : getStartOfCurrentWeekUTC()
    const currentPeriodEnd = monthly ? getEndOfCurrentMonthUTC() : getEndOfCurrentWeekUTC()
    const incomingOrdersQuantity = variants
      .flatMap((v) => v.incomingOrders)
      .filter(
        (o) =>
          o.expectedArrivalDate >= currentPeriodStart && o.expectedArrivalDate <= currentPeriodEnd,
      )
      .reduce((sum, o) => sum + o.quantity, 0)

    const addToCurrentPeriod = (arr: typeof closingStock) =>
      arr.map((entry) =>
        entry.date.getTime() === currentPeriodStart.getTime()
          ? { ...entry, quantity: entry.quantity + incomingOrdersQuantity }
          : entry,
      )

    return {
      closingStock: addToCurrentPeriod(closingStock),
      closingStockWithOrders: addToCurrentPeriod(closingStockWithOrders),
    }
  }, [closingStockData, variants, monthly])

  const { data: company } = api.company.getCompany.useQuery()
  const returnsEnabled = company?.returnsSettings?.enabled ?? false

  const { data: forecastedReturnsData } =
    api.expectedReturns.getForecastedReturnsForVariants.useQuery(
      { variantIds, warehouseId, channel },
      { enabled: returnsEnabled },
    )

  const suggestedIncomingStockGraphData = useMemo(
    (): DateChartSeries | undefined =>
      closingStockData.orders.length > 0
        ? dateSeries(chartConfig.incomingStockSuggested, {
            name: "Suggested incoming stock",
            type: "column",
            ...INBOUND_STACK,
            data: generateOrdersSeries(
              startDate,
              endDate,
              closingStockData.orders.map((o) => ({
                date: o.expectedArrivalDate,
                quantity: o.quantity,
              })),
              monthly,
            ),
          })
        : undefined,
    [closingStockData.orders, chartConfig, startDate, endDate, monthly],
  )

  const incomingStockGraphData = useMemo((): DateChartSeries | undefined => {
    const incomingStock = variants
      .flatMap((x) => x.incomingOrders)
      .map((x) => ({ date: x.expectedArrivalDate, quantity: x.quantity }))
    return incomingStock.length > 0
      ? dateSeries(chartConfig.incomingStock, {
          name: "Incoming stock",
          type: "column",
          ...INBOUND_STACK,
          data: generateOrdersSeries(startDate, endDate, incomingStock, monthly),
        })
      : undefined
  }, [variants, chartConfig, startDate, endDate, monthly])

  const expectedReturnsGraphData = useMemo(
    (): DateChartSeries | undefined =>
      returnsEnabled && forecastedReturnsData
        ? dateSeries(chartConfig.expectedReturns, {
            name: "Expected returns",
            type: "column",
            ...INBOUND_STACK,
            data: generateOrdersSeries(
              startDate,
              endDate,
              forecastedReturnsData.map((r) => ({ date: new Date(r.date), quantity: r.quantity })),
              monthly,
            ),
          })
        : undefined,
    [returnsEnabled, forecastedReturnsData, chartConfig, startDate, endDate, monthly],
  )

  if (variants.length <= 0)
    return {
      isLoading: closingStockData.isLoading,
      inventoryPastGraphData: {} as DateChartSeries,
      inventoryFutureGraphData: {} as DateChartSeries,
      safetyStockGraphData: {} as DateChartSeries,
      optimalProjectedStockGraphData: {} as DateChartSeries,
      suggestedIncomingStockGraphData: {} as DateChartSeries,
      incomingStockGraphData: {} as DateChartSeries,
      expectedReturnsGraphData: undefined,
      xPlotLinesInventory: [] as PlotLine[],
    }

  const futureOutOfStockInfo = filterDefined(
    variants
      .flatMap((v) => v.outOfStockInformation.flatMap((s) => s.stockoutDates))
      .filter((date) => date && isAfter(date, startOfTodayUTC())),
  )

  const firstStockoutDate = new Date(
    futureOutOfStockInfo.reduce((min, date) => Math.min(min, date.getTime()), Infinity),
  )
  const firstStockoutSku = variants
    .flatMap((v) => v.outOfStockInformation.flatMap((s) => s))
    .find((sku) => sku.stockoutDates.find((x) => x.getTime() === firstStockoutDate.getTime()))

  const optimalProjectedStockGraphData: DateChartSeries = dateSeries(
    chartConfig.closingStockSuggested,
    {
      name: "Suggested closing stock",
      type: "spline",
      data: generateInventorySeries(
        monthly ? getStartOfCurrentMonthUTC() : getStartOfCurrentWeekUTC(),
        endDate,
        closingStockWithIncomingOrdersCurrentPeriod.closingStockWithOrders,
        monthly,
      ),
    },
  )

  // INVENTORY - PAST
  // Mask periods before the first known inventory record to null so the closing-stock line
  // starts at the first inventory date instead of drawing a misleading flat-0 area (mirrors
  // the ClosingStock/WeeksOnHand table dashes; Highcharts renders null points as a gap).
  const currentPeriodStart = monthly ? getStartOfCurrentMonthUTC() : getStartOfCurrentWeekUTC()
  const inventoryPastGraphData: DateChartSeries = dateSeries(chartConfig.closingStock, {
    name: "Closing stock",
    type: "areaspline",
    data: generateInventorySeries(
      startDate,
      monthly ? getEndOfCurrentMonthUTC() : getEndOfCurrentWeekUTC(),
      closingStockWithIncomingOrdersCurrentPeriod.closingStock,
      monthly,
    ).map((point) =>
      isBeforeFirstKnownInventory(
        new Date(point.milliseconds),
        currentPeriodStart,
        closingStockData.firstInventoryDate,
      )
        ? { ...point, value: null }
        : point,
    ),
  })

  // INVENTORY - FUTURE
  const inventoryFutureGraphData: DateChartSeries = dateSeries(chartConfig.projectedStock, {
    name: "Projected stock",
    type: "areaspline",
    data: generateInventorySeries(
      monthly ? getStartOfCurrentMonthUTC() : getStartOfCurrentWeekUTC(),
      endDate,
      closingStockWithIncomingOrdersCurrentPeriod.closingStock,
      monthly,
    ),
    showNegativeColor: true,
  })

  const safetyStockGraphData: DateChartSeries = dateSeries(chartConfig.safetyStock, {
    name: "Safety stock",
    type: "spline",
    data: safetyStockSeries.map((x) => ({
      milliseconds: x.date.getTime(),
      value: Math.ceil(x.quantity),
    })),
  })

  const xPlotLinesInventory: PlotLine[] = []
  const stockOutDate = closingStockData.closingStock.find(
    (closingStock) => closingStock.quantity <= 0 && isAfter(closingStock.date, startOfTodayUTC()),
  )?.date

  // const reorderDate =
  //   variants.length > 0
  //     ? _.min(
  //         variants.flatMap((v) => {
  //           return v.outOfStockInformation.map((o) => {
  //             return o.safetyStockOutDate
  //               ? subDays(o.safetyStockOutDate, v.settings?.totalLeadTimeDays ?? 35)
  //               : undefined
  //           })
  //         }),
  //       )
  //     : undefined

  if (stockOutDate) {
    xPlotLinesInventory.push({
      value: stockOutDate.getTime(),
      lineColor: "rgba(248,38,63,0.8)",
      text: "Stockout",
      textColor: "#FFF",
      backgroundColor: "rgba(248,38,63)",
      showOnHover: true,
      offset: { x: 0 },
      rotation: 90,
    })
  }

  const stockOutLabel = () => {
    /*if (skuIds.length === 1) {
      return firstStockoutSku?.productName
    } else if (variantIds.length > 1) {
      return `${formatVariantId(firstStockoutSku?.variantId)} - ${firstStockoutSku?.size}`
    } else {
      return formatSkuId(firstStockoutSku?.skuId)
    }*/

    return formatSkuId(firstStockoutSku?.skuId)
  }

  if (firstStockoutSku) {
    xPlotLinesInventory.push({
      value: firstStockoutDate.getTime(),
      lineColor: "rgba(248,38,63,0.8)",
      text: "Next SKU Stockout: " + (stockOutLabel() ?? ""),
      textColor: "#FFF",
      backgroundColor: "rgba(248,38,63)",
      showOnHover: true,
      offset: { x: 0 },
      rotation: 90,
    })
  }

  // if (reorderDate) {
  //   xPlotLinesInventory.push({
  //     value: reorderDate.getTime(),
  //     lineColor: "rgba(79,207,223,0.8)",
  //     text: "Reorder",
  //     textColor: "#FFF",
  //     backgroundColor: "rgba(79,207,223)",
  //     showOnHover: true,
  //     rotation: 90,
  //     offset: { x: 0 },
  //   })
  // }

  return {
    isLoading: closingStockData.isLoading,
    inventoryPastGraphData,
    inventoryFutureGraphData,
    safetyStockGraphData,
    optimalProjectedStockGraphData,
    xPlotLinesInventory,
    suggestedIncomingStockGraphData,
    incomingStockGraphData,
    expectedReturnsGraphData,
  }
}
